import { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import {
  MOTIVOS, buscarPersona, fechaHora, hora, listaCasas, obtenerCasa, registrarIngreso, texto as textoDe,
} from "../services/seguridadService";

const VACIO = {
  tipo: "PEATONAL",
  categoria: "VISITA",
  persona: null,
  identificacion: "",
  nombre: "",
  placa: "",
  filial: null,
  persona_visitada: "",
  motivo: "VISITA",
  notas: "",
  autorizacion: null,     // visita que dejó autorizada un residente
  autorizado_por: "",
};

/**
 * Registro de ingreso, vehicular o peatonal. Al digitar la cédula (o la placa)
 * se busca si es alguien de una casa o un visitante frecuente y el formulario
 * se llena solo. También llega prellenado desde la búsqueda ("Ingreso" de un
 * autorizado, o "Visita" a una casa).
 */
export default function GaritaRegistrar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { recargarAdentro } = useOutletContext();
  const L = useLang();
  const [form, setForm] = useState(() => desdeEstado(location.state));
  const [casas, setCasas] = useState([]);
  const [casa, setCasa] = useState(null);            // casa elegida, con su gente
  const [otraPersona, setOtraPersona] = useState(false);
  const [sugerencia, setSugerencia] = useState(null);  // lo que encontró buscar-persona
  const [buscando, setBuscando] = useState(false);
  const [errores, setErrores] = useState({});
  const [aviso, setAviso] = useState(null);           // { tipo: ok|error, texto, abierto? }
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    listaCasas().then(setCasas).catch(() => setCasas([]));
  }, []);

  // Al cambiar de casa, traer su gente para escoger a quién visita.
  useEffect(() => {
    if (!form.filial) { setCasa(null); return; }
    obtenerCasa(form.filial).then(setCasa).catch(() => setCasa(null));
  }, [form.filial]);

  function set(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
    setErrores((e) => ({ ...e, [campo]: undefined }));
  }

  function cambiarIdentificacion(valor) {
    setForm((f) => (f.categoria !== "VISITA"
      // Otra cédula = otra persona: deja de ser el autorizado que estaba cargado.
      ? { ...f, identificacion: valor, categoria: "VISITA", persona: null, motivo: "VISITA" }
      : { ...f, identificacion: valor }));
    setSugerencia(null);
    setAviso(null);
    setErrores((e) => ({ ...e, identificacion: undefined }));
  }

  async function buscar(params) {
    const valor = Object.values(params)[0];
    if (!valor || String(valor).trim().length < 3) return;
    setBuscando(true);
    try {
      const r = await buscarPersona(params);
      if (!r.origen) { setSugerencia(null); return; }
      setSugerencia(r);
      setForm((f) => {
        if (r.origen === "temporal") {
          return {
            ...f,
            categoria: "VISITA",
            persona: null,
            autorizacion: r.id,
            autorizado_por: r.autorizado_por || "",
            identificacion: f.identificacion || r.identificacion,
            nombre: f.nombre || r.nombre,
            placa: f.placa || r.placa || "",
            filial: r.filial_id,
            persona_visitada: f.persona_visitada || r.autorizado_por || "",
            motivo: r.motivo || f.motivo,
          };
        }
        if (r.origen === "residente") {
          return {
            ...f,
            categoria: r.categoria,
            persona: r.persona_id,
            identificacion: r.identificacion,
            nombre: r.nombre,
            placa: f.placa || r.placa || "",
            filial: r.filial_id,
            motivo: "RESIDENTE",
          };
        }
        return {
          ...f,
          identificacion: f.identificacion || r.identificacion,
          nombre: f.nombre || r.nombre,
          placa: f.placa || r.placa || "",
          filial: f.filial || r.filial_id,
          persona_visitada: f.persona_visitada || r.persona_visitada,
          motivo: r.motivo && r.motivo !== "RESIDENTE" ? r.motivo : f.motivo,
        };
      });
    } catch { /* sin sugerencia: se llena a mano */ } finally {
      setBuscando(false);
    }
  }

  function validar() {
    const e = {};
    if (!form.identificacion.trim()) e.identificacion = L("La identificación es obligatoria.", "ID is required.");
    if (!form.nombre.trim()) e.nombre = L("El nombre es obligatorio.", "Name is required.");
    if (form.tipo === "VEHICULAR" && !form.placa.trim()) e.placa = L("La placa es obligatoria.", "Plate is required.");
    if (!form.filial) e.filial = L("Escogé la casa que visita.", "Choose the unit being visited.");
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  async function guardar() {
    setAviso(null);
    if (!validar()) return;
    setGuardando(true);
    try {
      const r = await registrarIngreso(form);
      setAviso({ tipo: "ok", texto: L(`Ingreso registrado: ${r.nombre} a ${r.filial_nombre}, ${hora(r.entrada)}`,
                                       `Entry registered: ${r.nombre} to ${r.filial_nombre}, ${hora(r.entrada)}`) });
      setForm((f) => ({ ...VACIO, tipo: f.tipo }));
      setSugerencia(null);
      setOtraPersona(false);
      recargarAdentro();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (e.data?.errores) setErrores(e.data.errores);
      setAviso({ tipo: "error", texto: e.message, abierto: e.status === 409 });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setGuardando(false);
    }
  }

  const esVisita = form.categoria === "VISITA";
  const gente = (casa?.personas || []).map((p) => p.nombre);
  // Un nombre que no es de la lista (p. ej. quien autorizó desde el portal) se
  // muestra en el campo de "Otra persona".
  const escribiendoOtra = otraPersona || (!!form.persona_visitada && !!casa && !gente.includes(form.persona_visitada));
  const relacion = form.categoria === "AUTORIZADO"
    ? (casa?.personas || []).find((p) => p.persona_id === form.persona)?.relacion || sugerencia?.relacion
    : "";

  return (
    <>
      <h1 className="gar-titulo">{L("Registrar ingreso", "Register entry")}</h1>

      {aviso && (
        <div className={`gar-aviso ${aviso.tipo}`} style={{ marginBottom: 12 }}>
          <i className={`fa-solid ${aviso.tipo === "ok" ? "fa-circle-check" : "fa-triangle-exclamation"}`} />
          <div style={{ flex: 1 }}>
            {aviso.texto}
            {aviso.abierto && (
              <div style={{ marginTop: 8 }}>
                <button type="button" className="gar-btn gar-btn-sec gar-btn-chico"
                  onClick={() => navigate("/garita/adentro")}>
                  {L("Ver quién está adentro", "See who is inside")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="gar-stack">
        <div className="gar-switch" role="group" aria-label={L("Tipo de ingreso", "Entry type")}>
          <button type="button" className={form.tipo === "PEATONAL" ? "activo" : ""} onClick={() => set("tipo", "PEATONAL")}>
            <i className="fa-solid fa-person-walking" /> {textoDe("PEATONAL")}
          </button>
          <button type="button" className={form.tipo === "VEHICULAR" ? "activo" : ""} onClick={() => set("tipo", "VEHICULAR")}>
            <i className="fa-solid fa-car-side" /> {textoDe("VEHICULAR")}
          </button>
        </div>

        <div className="gar-card gar-stack">
          <div className="gar-grid-2">
            <div>
              <label className="gar-label" htmlFor="gar-ident">{L("Identificación", "ID")} *</label>
              <div className="gar-fila" style={{ flexWrap: "nowrap" }}>
                <input id="gar-ident" className={`gar-input ${errores.identificacion ? "error" : ""}`}
                  inputMode="text" autoComplete="off" placeholder={L("Cédula o documento", "ID or document")}
                  value={form.identificacion}
                  onChange={(e) => cambiarIdentificacion(e.target.value)}
                  onBlur={() => buscar({ identificacion: form.identificacion.trim() })} />
                <button type="button" className="gar-icono-btn" style={{ width: 48, height: 48 }}
                  onClick={() => buscar({ identificacion: form.identificacion.trim() })} aria-label={L("Buscar cédula", "Look up ID")}>
                  <i className={`fa-solid ${buscando ? "fa-circle-notch fa-spin" : "fa-magnifying-glass"}`} />
                </button>
              </div>
              {errores.identificacion && <div className="gar-error">{errores.identificacion}</div>}
            </div>
            <div>
              <label className="gar-label" htmlFor="gar-nombre">{esVisita ? L("Nombre del visitante", "Visitor name") : L("Nombre", "Name")} *</label>
              <input id="gar-nombre" className={`gar-input ${errores.nombre ? "error" : ""}`}
                autoComplete="off" placeholder={L("Nombre y apellidos", "Full name")}
                value={form.nombre} onChange={(e) => set("nombre", e.target.value)} />
              {errores.nombre && <div className="gar-error">{errores.nombre}</div>}
            </div>
          </div>

          {!esVisita && (
            <div className="gar-aviso ok">
              <i className="fa-solid fa-user-check" />
              <span>
                <strong>{textoDe(form.categoria)} {L("de", "of")} {casa?.idFilial || sugerencia?.filial || ""}</strong>
                {relacion ? ` (${textoDe(relacion)})` : ""}.{" "}
                {L("Se registra como ingreso de la casa.", "It is registered as an entry of the unit.")}
              </span>
            </div>
          )}
          {form.autorizacion && (
            <div className="gar-aviso ok">
              <i className="fa-solid fa-calendar-check" />
              <span>
                <strong>{L("Visita autorizada por el residente", "Visit authorized by the resident")}</strong>
                {form.autorizado_por ? ` (${form.autorizado_por})` : ""}.{" "}
                {!form.identificacion.trim()
                  ? L("Pedile la cédula y anotala.", "Ask for the ID and type it in.")
                  : L("Revisá que la cédula coincida.", "Check that the ID matches.")}
              </span>
            </div>
          )}
          {sugerencia?.origen === "frecuente" && (
            <div className="gar-aviso info">
              <i className="fa-solid fa-clock-rotate-left" />
              <span>
                <strong>{L("Visitante frecuente:", "Frequent visitor:")}</strong>{" "}
                {L(`${sugerencia.visitas} visita(s), la última el ${fechaHora(sugerencia.ultima_visita)} a ${sugerencia.filial}. Revisá que los datos sigan igual.`,
                   `${sugerencia.visitas} visit(s), last one on ${fechaHora(sugerencia.ultima_visita)} to ${sugerencia.filial}. Check the details are still right.`)}
              </span>
            </div>
          )}

          {form.tipo === "VEHICULAR" && (
            <div>
              <label className="gar-label" htmlFor="gar-placa">{L("Placa", "Plate")} *</label>
              <input id="gar-placa" className={`gar-input ${errores.placa ? "error" : ""}`}
                autoComplete="off" placeholder="Ej: ABC-123" style={{ fontWeight: 800 }}
                value={form.placa} onChange={(e) => set("placa", e.target.value.toUpperCase())}
                onBlur={() => !form.identificacion.trim() && buscar({ placa: form.placa.trim() })} />
              {errores.placa && <div className="gar-error">{errores.placa}</div>}
            </div>
          )}

          <div>
            <label className="gar-label">{esVisita ? L("Casa que visita", "Unit visited") : L("Casa", "Unit")} *</label>
            <CasaPicker
              casas={casas}
              value={form.filial}
              error={errores.filial}
              onChange={(id) => {
                set("filial", id);
                setOtraPersona(false);
                // La autorización es de una casa: si cambia la casa, deja de valer.
                if (id !== form.filial) setForm((f) => ({ ...f, autorizacion: null, autorizado_por: "" }));
              }}
            />
          </div>

          {/* Alguien de la casa entra a su propia casa: no visita a nadie. */}
          {form.filial && esVisita && (
            <div>
              <label className="gar-label">{L("Persona a quien visita", "Person visited")}</label>
              <div className="gar-chips">
                {gente.map((n) => (
                  <button type="button" key={n}
                    className={`gar-chip ${!otraPersona && form.persona_visitada === n ? "activo" : ""}`}
                    onClick={() => { setOtraPersona(false); set("persona_visitada", n); }}>
                    {n}
                  </button>
                ))}
                <button type="button" className={`gar-chip ${escribiendoOtra ? "activo" : ""}`}
                  onClick={() => { setOtraPersona(true); set("persona_visitada", ""); }}>
                  <i className="fa-solid fa-pen" /> {L("Otra persona", "Someone else")}
                </button>
              </div>
              {escribiendoOtra && (
                <input className="gar-input" style={{ marginTop: 8 }} placeholder={L("Nombre de la persona", "Person's name")}
                  value={form.persona_visitada} onChange={(e) => set("persona_visitada", e.target.value)} />
              )}
            </div>
          )}

          {esVisita && (
            <div>
              <label className="gar-label">{L("Motivo de la visita", "Reason for visit")}</label>
              <div className="gar-chips">
                {MOTIVOS.map((m) => (
                  <button type="button" key={m.value}
                    className={`gar-chip ${form.motivo === m.value ? "activo" : ""}`}
                    onClick={() => set("motivo", m.value)}>
                    <i className={`fa-solid ${m.icon}`} /> {L(m.es, m.en)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="gar-label" htmlFor="gar-notas">{L("Notas", "Notes")}</label>
            <textarea id="gar-notas" className="gar-textarea" placeholder={L("Opcional", "Optional")}
              value={form.notas} onChange={(e) => set("notas", e.target.value)} />
          </div>
        </div>

        <button type="button" className="gar-btn gar-btn-primario gar-btn-grande gar-btn-bloque"
          disabled={guardando} onClick={guardar}>
          <i className={`fa-solid ${guardando ? "fa-circle-notch fa-spin" : "fa-right-to-bracket"}`} />
          {guardando ? L("Registrando…", "Registering…") : L("Registrar ingreso", "Register entry")}
        </button>
      </div>
    </>
  );
}

// Selector de casa para el dedo: se escribe y aparecen botones grandes.
function CasaPicker({ casas, value, onChange, error }) {
  const L = useLang();
  const [texto, setTexto] = useState("");
  const elegida = casas.find((c) => c.id === value);
  if (elegida) {
    return (
      <div className="gar-fila" style={{ flexWrap: "nowrap", padding: "8px 10px 8px 14px", borderRadius: 12,
        border: "1px solid var(--gar-acento)", background: "rgba(var(--gar-acento-rgb),0.1)" }}>
        <i className="fa-solid fa-house" style={{ color: "var(--gar-acento)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: 17 }}>{elegida.idFilial}</strong>
          {elegida.propietario && <div style={{ color: "var(--gar-suave)", fontSize: 13 }}>{elegida.propietario}</div>}
        </div>
        <button type="button" className="gar-btn gar-btn-sec gar-btn-chico" onClick={() => onChange(null)}>{L("Cambiar", "Change")}</button>
      </div>
    );
  }
  const t = texto.trim().toLowerCase();
  // Primero la casa exacta, después las que empiezan igual (en un condominio
  // grande, "12" tiene que mostrar la casa 12 antes que la 112).
  const rango = (c) => {
    const n = c.idFilial.toLowerCase();
    return n === t ? 0 : n.startsWith(t) ? 1 : 2;
  };
  const lista = casas.filter((c) => !t || c.idFilial.toLowerCase().includes(t)
    || (c.propietario || "").toLowerCase().includes(t))
    .sort((a, b) => rango(a) - rango(b)).slice(0, 8);
  return (
    <>
      <input className={`gar-input ${error ? "error" : ""}`} placeholder={L("Escribí la casa o el propietario", "Type the unit or the owner")}
        value={texto} onChange={(e) => setTexto(e.target.value)} autoComplete="off" />
      {error && <div className="gar-error">{error}</div>}
      {t && (
        <div className="gar-stack" style={{ gap: 6, marginTop: 8 }}>
          {lista.length === 0 && <div style={{ color: "var(--gar-suave)", fontSize: 14 }}>{L(`No hay casas con "${texto}".`, `No units match "${texto}".`)}</div>}
          {lista.map((c) => (
            <button type="button" key={c.id} className="gar-btn gar-btn-sec gar-btn-bloque"
              style={{ justifyContent: "flex-start", fontWeight: 700 }} onClick={() => onChange(c.id)}>
              <span style={{ fontSize: 16, fontWeight: 900 }}>{c.idFilial}</span>
              {c.propietario && <span style={{ color: "var(--gar-suave)", fontWeight: 600 }}>{c.propietario}</span>}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// Lo que manda la búsqueda al tocar "Ingreso" o "Visita".
function desdeEstado(st) {
  if (!st) return VACIO;
  const categoria = st.categoria || "VISITA";
  return {
    ...VACIO,
    tipo: st.placa ? "VEHICULAR" : "PEATONAL",
    categoria,
    persona: st.persona || null,
    identificacion: st.identificacion || "",
    nombre: st.nombre || "",
    placa: st.placa || "",
    filial: st.casa || null,
    persona_visitada: st.persona_visitada || "",
    motivo: categoria === "VISITA" ? (st.motivo || "VISITA") : "RESIDENTE",
    autorizacion: st.autorizacion || null,
    autorizado_por: st.autorizado_por || "",
  };
}
